import {
  Box,
  Flex,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Spacer,
  Text,
  Tooltip,
  useBoolean,
  useOutsideClick,
} from '@chakra-ui/react'
import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
// import { atomWithStorage } from 'jotai/utils'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactPlayer from 'react-player'
import { OnProgressProps } from 'react-player/base'
import { useNavigate } from 'react-router'
import IconDownload from '~/assets/svgs/icon-download.svg?react'
import IconFastForward10 from '~/assets/svgs/icon-fast-forward-10.svg?react'
import IconInfo from '~/assets/svgs/icon-info.svg?react'
import IconJumpTo from '~/assets/svgs/icon-jump-to.svg?react'
import IconPause from '~/assets/svgs/icon-pause.svg?react'
import IconPbs1_0x from '~/assets/svgs/icon-pbs-1.0x.svg?react'
import IconPbs1_5x from '~/assets/svgs/icon-pbs-1.5x.svg?react'
import IconPbs1_25x from '~/assets/svgs/icon-pbs-1.25x.svg?react'
import IconPbs1_75x from '~/assets/svgs/icon-pbs-1.75x.svg?react'
import IconPbs2_0x from '~/assets/svgs/icon-pbs-2.0x.svg?react'
import IconPlayArrow from '~/assets/svgs/icon-play-arrow.svg?react'
import IconRewind10 from '~/assets/svgs/icon-rewind-10.svg?react'
import IconSpeakerMuted from '~/assets/svgs/icon-speaker-muted.svg?react'
import IconSpeaker from '~/assets/svgs/icon-speaker.svg?react'
import { StorageKeys } from '~/atoms/StorageKeys'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { useKeyPresses } from '~/hooks/useKeyboard'
import { ApplicationRoutes } from '~/hooks/useRouting'
import { textStyleKeys } from '~/themes/textStyles'
import { AudioData } from '~/types/AudioPlayerTypes'
import { SettingsType } from '~/types/Settings'
import { Size } from '~/types/ThemeTypes'
import { hasValue } from '~/utils/common'
import {
  convertCallDurationNoMilliseconds,
  formatMilliseconds,
  tripLeadingZeroFromTime,
} from '~/utils/formatter'

import {
  AudioPlayerVariant,
  useAudioPlayerStyle,
} from './componentStyles/AudioPlayer.style'
import { DropdownActionButton } from './Dropdown/DropdownActionButton'
import { LoadingSpinner } from './LoadingSpinner'

const playbackSpeedAtom = atomWithStorage(
  StorageKeys.AudioPlayerPlaybackSpeed,
  0,
)

const playbackSpeeds: PlaybackSpeedIconData[] = [
  {
    speed: 1.0,
    speedText: '1.0x',
    icon: IconPbs1_0x,
    iconWidth: '1.25rem',
  },
  {
    speed: 1.25,
    speedText: '1.25x',
    icon: IconPbs1_25x,
    iconWidth: '2.5rem',
  },
  {
    speed: 1.5,
    speedText: '1.5x',
    icon: IconPbs1_5x,
    iconWidth: '2rem',
  },
  {
    speed: 1.75,
    speedText: '1.75x',
    icon: IconPbs1_75x,
    iconWidth: '2.5rem',
  },
  {
    speed: 2.0,
    speedText: '2.0x',
    icon: IconPbs2_0x,
    iconWidth: '1.25rem',
  },
]

interface AudioPlayerState {
  isMediaReady?: boolean
  isPlaying?: boolean
  isFinishedBuffering?: boolean
  scrubbing?: boolean
  muted?: boolean
  requestedSeekAndPlayTime?: number | null
  keyboardInputEnabled?: boolean
  audioData?: AudioData | null
  volume?: number
  playbackRate?: number | null
}

interface PlaybackSpeedIconData {
  speed: number
  speedText: string
  icon: SVGComponent
  iconWidth: string
}

interface AudioPlayerProps {
  audioUri: string | undefined
  playerTitle?: string
  answerTimestamp?: number
  requestedTimestamp?: number
  disabled?: boolean
  showInfoButton?: boolean
  showInlineInfoButton?: boolean
  showJumpToAnswerButton?: boolean
  showDownloadButton?: boolean
  transcriptionText?: string
  onReady?: () => void
  onLoadError?: () => void
  onValueChanged?: (value: number) => void
  onPlayStateChange?: (isPlaying: boolean) => void
  variant: AudioPlayerVariant
  requestedIsPlaying?: boolean
}
export const AudioPlayer = ({
  audioUri,
  playerTitle,
  answerTimestamp,
  requestedTimestamp,
  disabled,
  showInfoButton = true,
  showJumpToAnswerButton = true,
  showDownloadButton = false,
  showInlineInfoButton = false,
  transcriptionText,
  onReady,
  onLoadError,
  onValueChanged,
  onPlayStateChange: handlePlayStateChange,
  variant,
  requestedIsPlaying = false,
}: AudioPlayerProps) => {
  const [playerPosition, setPlayerPosition] = useState(0)
  const [playerState, setPlayerState] = useState<AudioPlayerState>({})
  const [mediaReady, setMediaReady] = useState(false)
  const [mediaErrored, setMediaErrored] = useState(false)
  const [player, setPlayer] = useState<ReactPlayer | null>(null)
  const [selectedPlaybackSpeedIndex, setSelectedPlaybackSpeed] =
    useAtom(playbackSpeedAtom)
  const [playbackSpeedMenuOpen, setPlaybackSpeedMenuState] = useBoolean()
  const [isThumbHovered, setIsThumbHovered] = useState(false)

  const boxRef = React.useRef<HTMLDivElement>(null)

  const scrubShortcutLengthMillis = 10 * 1000
  const shortScrubtShortcutLengthMillis = 3 * 1000
  const keyPress = useKeyPresses()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const formattedScrubberPosition = useMemo(() => {
    return tripLeadingZeroFromTime(formatMilliseconds(playerPosition))
  }, [playerPosition])

  const setPlayerRef = (playerRef: ReactPlayer) => {
    setPlayer(playerRef)
  }

  const setIsReady = () => {
    setMediaReady(true)
    onReady?.()
  }

  const setIsBuffering = () => {
    setPlayerState({ ...playerState, isFinishedBuffering: false })
  }

  const setDoneBuffering = () => {
    setPlayerState({ ...playerState, isFinishedBuffering: true })
  }

  const onPlayerLoadError = () => {
    setMediaErrored(true)
    onLoadError?.()
  }

  const setDuration = (duration: number) => {
    const milliseconds = Math.floor(duration * 1000)
    setPlayerState({
      ...playerState,
      audioData: {
        seconds: duration,
        milliseconds: milliseconds,
        formattedTime: formatMilliseconds(milliseconds),
      },
    })
  }

  const handleSetPlayerPosition = (position: number) => {
    onValueChanged?.(position)
    setPlayerPosition(position)
  }

  const setProgress = (progress: OnProgressProps) => {
    const playedMillis = progress.playedSeconds * 1000
    handleSetPlayerPosition(playedMillis)
    setPlayerState({
      ...playerState,
      scrubbing: false,
    })
  }

  const scrubToMillis = (toMillis: number) => {
    if (toMillis < 0) toMillis = 0
    const clipLengthMillis = playerState.audioData?.milliseconds ?? 0
    if (toMillis > clipLengthMillis) toMillis = clipLengthMillis

    handleSetPlayerPosition(toMillis)
    const secondsVal = toMillis > 0 ? toMillis / 1000 : 0
    player?.seekTo(secondsVal, 'seconds')
    setPlayerState({
      ...playerState,
      scrubbing: true,
    })
  }

  const scrubFromDelta = (deltaMillis: number) => {
    scrubToMillis(playerPosition + deltaMillis)
  }

  const setAudioEnded = () => {
    if (!playerState.isPlaying) return

    setPlayerState({
      ...playerState,
      isPlaying: false,
    })
    handleSetPlayerPosition(0)
    player?.seekTo(0)
  }

  const setAudioMuted = (val: boolean) => {
    setPlayerState({
      ...playerState,
      muted: val,
    })
  }

  const setAudioPlaying = (val: boolean) => {
    setPlayerState({
      ...playerState,
      isPlaying: val,
    })
  }

  const handleSliderValueChanged = (val: number) => {
    onValueChanged?.(val)
    scrubToMillis(val)
  }

  useEffect(() => {
    if (playerState.isPlaying !== undefined) {
      handlePlayStateChange?.(playerState.isPlaying)
    }
  }, [playerState.isPlaying])

  // if the audioUri changes, pause the audio and reset the position
  useEffect(() => {
    setPlayerPosition(0)
    setAudioPlaying(false)
  }, [audioUri])

  // if the player is disabled, pause the audio
  // user must resume manually
  useEffect(() => {
    if (disabled) {
      setAudioPlaying(false)
    }
  }, [disabled])

  //watches for a requestedTimestamp to be set and handles state updates.
  useEffect(() => {
    if (requestedTimestamp === undefined || !player || !mediaReady) return

    scrubToMillis(requestedTimestamp)
  }, [requestedTimestamp, mediaReady])

  //watches for a requestedTimestamp to be set and handles state updates.
  useEffect(() => {
    if (!player || !mediaReady) return

    if (requestedIsPlaying !== playerState.isPlaying) {
      setAudioPlaying(requestedIsPlaying)
    }
  }, [mediaReady, requestedIsPlaying])

  //watch keypress change
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent | null) => {
      if (!e?.key) return

      const activeElement = document.activeElement
      const isInputFocused =
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          (activeElement as HTMLElement).isContentEditable)

      if (isInputFocused) {
        return
      }

      const val =
        e.altKey || e.shiftKey
          ? shortScrubtShortcutLengthMillis
          : scrubShortcutLengthMillis
      switch (e.key) {
        case 'ArrowLeft':
          scrubFromDelta(-val)
          break
        case 'ArrowRight':
          scrubFromDelta(val)
          break
        case 'm':
          setAudioMuted(!playerState.muted)
          break
        case ' ':
          setAudioPlaying(!playerState.isPlaying)
          //while the player is on screen, prevent scrolling behaviors from other scrollable divs with the spacebar
          e.preventDefault()
          break
        default:
          break
      }
    }

    if (disabled) {
      return
    }

    onKeyDown(keyPress)
  }, [keyPress])

  const handleSetSelectedPlaybackSpeed = (data: PlaybackSpeedIconData) => {
    setPlayerState({
      ...playerState,
      playbackRate: data.speed,
    })
    setSelectedPlaybackSpeed(playbackSpeeds.indexOf(data))
    setPlaybackSpeedMenuState.off()
  }

  const handleInfoButtonPressed = () => {
    setAudioPlaying(false)
    navigate(`${ApplicationRoutes.SETTINGS}/${SettingsType.SHORTCUTS}`)
  }

  const handleDownloadButtonClicked = async () => {
    if (!audioUri) return
    const link = document.createElement('a')
    link.href = audioUri
    link.setAttribute('download', 'voicemail')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  useOutsideClick({
    ref: boxRef,
    handler: () => {
      setPlaybackSpeedMenuState.off()
    },
  })

  const styles = useAudioPlayerStyle({ variant })

  const SliderComponent = useMemo(() => {
    return (
      <Flex sx={styles.playbackContainer}>
        <Flex width="100%" justifyContent="space-between">
          <Text sx={styles.playbackTimestampText}>
            {`${formattedScrubberPosition}`}
          </Text>
          <Text sx={styles.playbackTimestampText} textAlign="right">
            {tripLeadingZeroFromTime(
              playerState.audioData?.formattedTime ?? '0:00',
            )}
          </Text>
        </Flex>

        <Slider
          sx={styles.playbackSlider}
          value={playerPosition}
          min={0}
          max={playerState.audioData?.milliseconds}
          onChange={(val) => handleSliderValueChanged(val)}
          focusThumbOnChange={false}
        >
          <SliderTrack sx={styles.playbackSliderTrack}>
            <SliderFilledTrack sx={styles.playbackSliderFilledTrack} />
          </SliderTrack>
          <Tooltip
            hasArrow
            placement="top"
            isOpen={isThumbHovered}
            label={`${convertCallDurationNoMilliseconds(playerPosition / 1000)}`}
          >
            <SliderThumb
              sx={styles.playbackSliderThumbContainer}
              onMouseEnter={() => setIsThumbHovered(true)}
              onMouseLeave={() => setIsThumbHovered(false)}
              data-group
            >
              <Box sx={styles.playbackSliderVerticalBar} />
              <Box sx={styles.playbackSliderThumb}>
                <Box sx={styles.playbackSliderThumbInner} />
              </Box>
            </SliderThumb>
          </Tooltip>
        </Slider>
      </Flex>
    )
  }, [
    formattedScrubberPosition,
    playerPosition,
    playerState.audioData,
    isThumbHovered,
  ])

  const noAudio = !hasValue(audioUri)
  const AudioPlayerBodyContent = useMemo(() => {
    if (noAudio) {
      return (
        <Text
          alignSelf="center"
          justifySelf="center"
          textStyle={textStyleKeys.Body1_Bold}
        >
          No Recording Available
        </Text>
      )
    }

    if (mediaErrored) {
      return (
        <Flex sx={styles.errorContainer}>
          <Text sx={styles.errorText}>
            There was a problem loading this audio file.
          </Text>
        </Flex>
      )
    }
    if (!mediaReady) {
      return (
        <Flex sx={styles.loadingContainer}>
          <LoadingSpinner />
        </Flex>
      )
    }

    const inlineSlider = variant === AudioPlayerVariant.Inline
    return (
      <>
        <Flex sx={styles.controlContainer}>
          {/* Control Buttons */}
          <Flex sx={styles.controlButtonsContainer}>
            {playerState.muted && (
              <QuickActionButton
                variant={QuickActionButtonVariants.Default_100}
                actionIcon={IconSpeakerMuted}
                tooltipText={t('voicemail.quickActions.unmute')}
                onClick={() => setAudioMuted(false)}
              />
            )}
            {!playerState.muted && (
              <QuickActionButton
                variant={QuickActionButtonVariants.Default_100}
                actionIcon={IconSpeaker}
                tooltipText={t('voicemail.quickActions.mute')}
                onClick={() => setAudioMuted(true)}
              />
            )}
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_100}
              actionIcon={IconRewind10}
              tooltipText={t('voicemail.quickActions.rewind10s')}
              onClick={() => scrubFromDelta(-scrubShortcutLengthMillis)}
            />
            {playerState.isPlaying && (
              <QuickActionButton
                variant={QuickActionButtonVariants.Default_200}
                size={Size.lg}
                actionIcon={IconPause}
                tooltipText={t('voicemail.quickActions.pause')}
                iconProps={{
                  boxSize: '1.25rem',
                }}
                onClick={() => setAudioPlaying(false)}
              />
            )}
            {!playerState.isPlaying && (
              <QuickActionButton
                variant={QuickActionButtonVariants.Default_200}
                size={Size.lg}
                actionIcon={IconPlayArrow}
                tooltipText={t('voicemail.quickActions.play')}
                iconProps={{
                  boxSize: '1.25rem',
                }}
                onClick={() => setAudioPlaying(true)}
              />
            )}
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_100}
              actionIcon={IconFastForward10}
              tooltipText={t('voicemail.quickActions.fastForward10s')}
              onClick={() => scrubFromDelta(scrubShortcutLengthMillis)}
            />
            {showJumpToAnswerButton && answerTimestamp && (
              <QuickActionButton
                variant={QuickActionButtonVariants.Default_100}
                actionIcon={IconJumpTo}
                tooltipText={t('voicemail.quickActions.jumpToAnswer')}
                onClick={() => scrubToMillis(answerTimestamp)}
              />
            )}
            <DropdownActionButton
              variant={QuickActionButtonVariants.Default_100}
              icon={playbackSpeeds[selectedPlaybackSpeedIndex].icon}
              tooltipText={t('voicemail.quickActions.changePlaybackSpeed')}
              items={playbackSpeeds
                .map((pbs, _) => {
                  return {
                    value: { speed: pbs.speed },
                    content: pbs.speedText,
                    onSelect: () => handleSetSelectedPlaybackSpeed(pbs),
                  }
                })
                .filter((_, i) => i !== selectedPlaybackSpeedIndex)}
              iconProps={{
                boxSize: '2rem',
              }}
              menuOptions={{
                width: '6.25rem',
              }}
            />
          </Flex>
          {/* Playback Slider (Inline variant) */}
          {inlineSlider && SliderComponent}
          {/* Playback Speed */}
          <Flex
            ref={boxRef}
            position="relative"
            sx={styles.playbackSpeedContainer}
          >
            {showDownloadButton && (
              <QuickActionButton
                variant={QuickActionButtonVariants.Default_100}
                actionIcon={IconDownload}
                tooltipText={t('voicemail.quickActions.downloadAudio')}
                onClick={() => handleDownloadButtonClicked()}
              />
            )}
            {showInlineInfoButton && (
              <QuickActionButton
                variant={QuickActionButtonVariants.Default_100}
                actionIcon={IconInfo}
                tooltipText={t('voicemail.quickActions.seeKeyboardShortcuts')}
                onClick={handleInfoButtonPressed}
              />
            )}
          </Flex>
        </Flex>
        {/* Playback Slider (Stacked and Analytics variants) */}
        {!inlineSlider && SliderComponent}
        {transcriptionText && (
          <Flex sx={styles.transcriptionContainer}>
            <Text sx={styles.transcriptionTitleText}>
              {t('voicemail.transcriptionTitleText')}
            </Text>
            <Text sx={styles.transcriptionBodyText}>{transcriptionText}</Text>
          </Flex>
        )}
      </>
    )
  }, [
    playerState.muted,
    playerState.isPlaying,
    mediaErrored,
    mediaReady,
    playerState.audioData,
    selectedPlaybackSpeedIndex,
    playbackSpeedMenuOpen,
    variant,
    SliderComponent,
    transcriptionText,
  ])

  const playbackRate = playbackSpeeds[selectedPlaybackSpeedIndex].speed
  const shouldShowHeaderContainer =
    !noAudio && !mediaErrored && (hasValue(playerTitle) || showInfoButton)
  return (
    <Flex sx={styles.outerContainer}>
      <ReactPlayer
        url={audioUri}
        ref={setPlayerRef}
        height={0}
        width={0}
        playing={playerState.isPlaying}
        onPlay={() => setAudioPlaying(true)}
        onPause={() => setAudioPlaying(false)}
        onReady={setIsReady}
        onBuffer={setIsBuffering}
        onBufferEnd={setDoneBuffering}
        onDuration={setDuration}
        onProgress={setProgress}
        onEnded={setAudioEnded}
        onError={onPlayerLoadError}
        muted={playerState.muted}
        progressInterval={100}
        volume={playerState.volume}
        playbackRate={playbackRate}
      />
      <Flex sx={styles.container}>
        {shouldShowHeaderContainer && (
          <Flex sx={styles.headerContainer}>
            {playerTitle && <Text sx={styles.titleText}>{playerTitle}</Text>}
            <Spacer />
            {showInfoButton && (
              <QuickActionButton
                variant={QuickActionButtonVariants.Default_100}
                size={Size.sm}
                actionIcon={IconInfo}
                tooltipText={t('voicemail.quickActions.seeKeyboardShortcuts')}
                onClick={handleInfoButtonPressed}
              />
            )}
          </Flex>
        )}
        {AudioPlayerBodyContent}
      </Flex>
    </Flex>
  )
}
